import { z } from 'zod';
import { ALL_RESOURCE_IDS } from './resources.js';
/**
 * Contracts, Phase 4 — the factory's first real goal.
 *
 * Flow:
 *  1. Server keeps a rolling pool of `ContractOffer`s per player (P4: only
 *     CTA issues). Offers tick down; stale ones drop off.
 *  2. Player `acceptContract(id)` → offer moves to `activeContracts` with
 *     an absolute `deadlineTick` computed from `nowTick + durationTicks`.
 *  3. Each sim tick, the fulfillment hook drains matching resources from
 *     the Trade Port's input buffer into the active contract's `delivered`
 *     counter BEFORE the market-settle step — contracts always win over
 *     flat-price sales.
 *  4. When `delivered >= goal.quantity` the contract completes: payout
 *     credits + rep delta to the player. When `nowTick > deadlineTick`
 *     without completion, it expires (small rep penalty).
 *
 * Lore §12 guarantees contract payouts are +10–50% of market baseline.
 * P4A (market dynamics) will read the live price; P4 uses the static
 * `BASE_PRICE` table with a fixed 30% premium.
 */
// ── Goal shape ─────────────────────────────────────────────────────────────
export const ContractGoalSchema = z.object({
    resourceId: z.string(),
    quantity: z.number().int().positive(),
});
// ── Offer (unaccepted, visible on the board) ───────────────────────────────
export const ContractOfferSchema = z.object({
    id: z.string(),
    issuer: z.string(), // FactionId
    goal: ContractGoalSchema,
    /** Ticks between accept and deadline — absolute deadline set server-side on accept. */
    durationTicks: z.number().int().positive(),
    /** ₡ payout on completion. */
    payout: z.number().nonnegative(),
    /** Rep delta with the issuer on completion. */
    repDelta: z.number(),
    /** Offer disappears from the board at this absolute tick if not accepted. */
    offerExpiresTick: z.number().int().nonnegative(),
});
// ── Active (accepted, in-flight) ───────────────────────────────────────────
export const ActiveContractSchema = z.object({
    id: z.string(),
    issuer: z.string(), // FactionId
    goal: ContractGoalSchema,
    payout: z.number().nonnegative(),
    repDelta: z.number(),
    acceptedTick: z.number().int().nonnegative(),
    deadlineTick: z.number().int().nonnegative(),
    /** Units delivered so far. */
    delivered: z.number().int().nonnegative(),
});
/** Hard cap on simultaneously active contracts per player. */
export const MAX_ACTIVE_CONTRACTS = 5;
// ── Type narrowing helpers ─────────────────────────────────────────────────
export function isKnownResource(resourceId) {
    return ALL_RESOURCE_IDS.includes(resourceId);
}
export function isKnownFaction(factionId) {
    return ['cta', 'helion', 'argent', 'meridian', 'tycho', 'vega'].includes(factionId);
}
//# sourceMappingURL=contracts.js.map